import {Fragment} from 'react';
import PropTypes from 'prop-types';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {Dropdown, TextArea, TextInput} from '@/components/atoms';
import UploadProgressBar from '@/components/atoms/UploadProgressBar';

const CATEGORY_OPTIONS = [
  {displayText: 'Snacks', id: 'Snacks'},
  {displayText: 'Beverage', id: 'Beverage'}
];

const FIELD_GROUPS = [
  [
    {name: 'rackId', label: 'Rack ID', required: true},
    {name: 'displayName', label: 'Rack Display Name', required: true}
  ],
  [
    {name: 'materialCode', label: 'Material Code'},
    {name: 'category', label: 'Category'},
    {name: 'capacity', label: 'Capacity'},
    {name: 'width', label: 'Width'},
    {name: 'depth', label: 'Depth'},
    {name: 'height', label: 'Height'}
  ],
  [
    {
      name: 'points',
      label: 'Points for the Rack',
      type: 'number',
      required: true
    }
  ]
];

const RackModalForm = ({
  idPrefix,
  tabs,
  maxImages,
  activeTab,
  setActiveTab,
  images,
  rackInformation,
  uploadError,
  publishError,
  isDragging,
  isUploading,
  imageLimitReached,
  uploadDisabled,
  uploadProgress,
  fileInput,
  isBusy,
  readOnlyFields,
  imagesLabel,
  acceptHint,
  handleTabKeyDown,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleUploadClick,
  handleFileChange,
  handleRemoveImage,
  handleInformationChange,
  children
}) => {
  const categoryValue = rackInformation.category
    ? [{displayText: rackInformation.category, id: rackInformation.category}]
    : [];

  const handleCategoryChange = (selected) => {
    const option = Array.isArray(selected) ? selected[0] : selected;
    handleInformationChange('category', option?.id || '');
  };

  return (
    <div className='addRackContent'>
      <div className='addRackTabs' role='tablist' aria-label='Rack details'>
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            id={`${idPrefix}-tab-${tab.id}`}
            type='button'
            role='tab'
            aria-selected={activeTab === tab.id}
            aria-controls={`${idPrefix}-panel-${tab.id}`}
            tabIndex={activeTab === tab.id ? 0 : -1}
            onKeyDown={(event) => handleTabKeyDown(event, index)}
            onClick={() => setActiveTab(tab.id)}>
            {tab.label}
          </button>
        ))}
      </div>

      <div
        id={`${idPrefix}-panel-images`}
        role='tabpanel'
        aria-labelledby={`${idPrefix}-tab-images`}
        hidden={activeTab !== 'images'}>
        <div className='addRackImagesLabel'>
          {imagesLabel || 'Images*'}
          <InfoOutlinedIcon
            titleAccess={`Upload upto ${maxImages} JPG,JPEG,WEBP or PNG Images`}
          />
        </div>
        <div
          role='group'
          aria-label='Rack image upload'
          aria-busy={isUploading}
          className={`addRackDropzone${isDragging ? ' isDragging' : ''}${uploadError ? ' hasError' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}>
          <CloudUploadOutlinedIcon className='addRackUploadIcon' />
          <p>
            Drag and drop your file here
            <br />
            {acceptHint || '(only .webp,.jpg,.png)'}
          </p>
          <button
            type='button'
            className='addRackUploadButton'
            disabled={uploadDisabled}
            aria-describedby={
              imageLimitReached ? `${idPrefix}-image-limit-message` : undefined
            }
            onClick={handleUploadClick}>
            <FileUploadOutlinedIcon />
            Upload file
          </button>
          <input
            ref={fileInput}
            className='addRackFileInput'
            type='file'
            accept='.webp,.jpg,.jpeg,.png'
            multiple
            disabled={uploadDisabled}
            aria-label='Upload rack images'
            onChange={handleFileChange}
          />
        </div>
        <UploadProgressBar
          progress={uploadProgress}
          isProcessing={isUploading}
          processingLabel='Uploading rack images...'
        />
        {imageLimitReached && !uploadError && (
          <p id={`${idPrefix}-image-limit-message`} role='status'>
            Maximum of {maxImages} images reached. Remove an image to upload
            another.
          </p>
        )}
        {uploadError && (
          <p
            id={`${idPrefix}-image-limit-message`}
            className='addRackError'
            role='alert'>
            {uploadError}
          </p>
        )}
        {images.length > 0 && (
          <div className='addRackFiles'>
            <p className='addRackFilesLabel' aria-live='polite'>
              Uploaded files ({images.length}/{maxImages})
            </p>
            <ul>
              {images.map((file, index) => (
                <li key={`${file.name}-${file.lastModified}-${index}`}>
                  <div className='addRackFileDetails'>
                    <ImageOutlinedIcon />
                    <div>
                      <span className='addRackFileName'>{file.name}</span>
                      <span className='addRackFileSize'>
                        {file.size > 0
                          ? `${(file.size / (1024 * 1024)).toFixed(2)} MB`
                          : ''}
                      </span>
                    </div>
                  </div>
                  <button
                    type='button'
                    className='addRackDeleteButton'
                    disabled={isUploading || isBusy}
                    aria-label={`Remove ${file.name}`}
                    onClick={() => handleRemoveImage(index)}>
                    <DeleteOutlineIcon />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div
        id={`${idPrefix}-panel-information`}
        role='tabpanel'
        aria-labelledby={`${idPrefix}-tab-information`}
        hidden={activeTab !== 'information'}>
        {FIELD_GROUPS.map((group, groupIndex) => (
          <Fragment key={groupIndex}>
            <div className='addRackFieldGroup'>
              {group.map(({name, label, type, required}) =>
                name === 'category' ? (
                  <Dropdown
                    key={name}
                    id={`${idPrefix}-${name}`}
                    label={label}
                    isJsonWrap={false}
                    disabled={isBusy}
                    childList={CATEGORY_OPTIONS}
                    placeholder={`Select ${label}`}
                    selection='single'
                    size='medium'
                    isClearable={false}
                    isDark
                    showLabelIcon={false}
                    value={categoryValue}
                    updateValue={handleCategoryChange}
                  />
                ) : (
                  <TextInput
                    key={name}
                    id={`${idPrefix}-${name}`}
                    label={required ? `${label}*` : label}
                    isRequired={required}
                    disabled={isBusy || readOnlyFields?.includes(name)}
                    placeholderText={label}
                    type={type || 'text'}
                    isDark
                    value={rackInformation[name] || ''}
                    onChange={(value) => handleInformationChange(name, value)}
                  />
                )
              )}
            </div>
            {groupIndex === 0 && (
              <div className='addRackFieldGroup addRackDescriptionGroup'>
                <TextArea
                  id={`${idPrefix}-description`}
                  label='Description'
                  value={rackInformation.description || ''}
                  updateValue={(value) =>
                    handleInformationChange('description', value)
                  }
                  uiSchema={{
                    placeholder: 'Description',
                    showToolbar: false
                  }}
                  disabled={isBusy}
                />
              </div>
            )}
          </Fragment>
        ))}
      </div>

      {children}

      {publishError && (
        <p className='addRackError' role='alert'>
          {publishError}
        </p>
      )}
    </div>
  );
};

RackModalForm.propTypes = {
  idPrefix: PropTypes.string.isRequired,
  tabs: PropTypes.array.isRequired,
  maxImages: PropTypes.number.isRequired,
  activeTab: PropTypes.string.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  images: PropTypes.array.isRequired,
  rackInformation: PropTypes.object.isRequired,
  uploadError: PropTypes.string,
  publishError: PropTypes.string,
  isDragging: PropTypes.bool,
  isUploading: PropTypes.bool,
  imageLimitReached: PropTypes.bool,
  uploadDisabled: PropTypes.bool,
  uploadProgress: PropTypes.number,
  fileInput: PropTypes.object,
  isBusy: PropTypes.bool,
  readOnlyFields: PropTypes.arrayOf(PropTypes.string),
  imagesLabel: PropTypes.string,
  acceptHint: PropTypes.string,
  handleTabKeyDown: PropTypes.func.isRequired,
  handleDragOver: PropTypes.func.isRequired,
  handleDragLeave: PropTypes.func.isRequired,
  handleDrop: PropTypes.func.isRequired,
  handleUploadClick: PropTypes.func.isRequired,
  handleFileChange: PropTypes.func.isRequired,
  handleRemoveImage: PropTypes.func.isRequired,
  handleInformationChange: PropTypes.func.isRequired,
  children: PropTypes.node
};

export default RackModalForm;
